import { ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { Header } from '@rossum/components/header/Header';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconRefresh } from '@rossum/ui/icons/tabler';
import { Button, SvgIcon, Tab, Tabs, Tooltip } from '@rossum/ui/material';
import { ActivitiesHeaderProps as ExtensionActivitiesHeaderProps } from '../../features/extensions/pages/activities';
import { useExtensionLogsFeatureSubscription } from '../../features/pricing/hooks/useExtensionsFeatureSubscription';
import { SettingsBreadcrumbs } from '../Settings/SettingsBreadcrumbs';
import {
  activitiesExtensionsPath,
  activitiesPath,
  activitiesRulesPath,
} from './routes';

// Extended props that support both extensions (legacy props) and generic usage (buttons)
type ActivitiesHeaderProps = Partial<ExtensionActivitiesHeaderProps> & {
  scrollableDivRef: React.RefObject<HTMLDivElement>;
  buttons?: ReactNode[];
  title: string;
  description: string;
};

export const ActivitiesHeader = ({
  scrollableDivRef,
  // Legacy props for extensions page
  searchValue,
  setSearchValue,
  refreshRuns,
  isRefetching,
  dataUpdatedAt,
  // Generic props for other pages (like rules)
  buttons,
  title,
  description,
}: ActivitiesHeaderProps) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const logsSubscription = useExtensionLogsFeatureSubscription();

  // Determine active tab based on current path
  const isRulesTab = location.pathname.includes('/rules');
  const currentTab = isRulesTab
    ? activitiesRulesPath()
    : activitiesExtensionsPath();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      history.push(newValue);
    },
    [history]
  );

  // If buttons are provided, use them; otherwise render legacy search/refresh buttons
  const headerButtons =
    buttons ??
    (searchValue !== undefined &&
    setSearchValue &&
    refreshRuns &&
    logsSubscription.canAccess
      ? [
          <SearchInput
            key="search-activities"
            value={searchValue}
            onChange={setSearchValue}
          />,
          <Tooltip
            key="refresh-activities-button"
            title={
              dataUpdatedAt
                ? intl.formatMessage(
                    {
                      id: 'containers.settings.extensions.logs.dataUpdatedAt',
                    },
                    {
                      date: intl.formatDate(dataUpdatedAt, {
                        dateStyle: 'medium',
                        timeStyle: 'medium',
                      }),
                    }
                  )
                : ''
            }
          >
            <Button
              data-cy="refresh-activities-button"
              variant="contained"
              onClick={!isRefetching ? refreshRuns : undefined}
              startIcon={
                <SvgIcon fontSize="large">
                  <IconRefresh />
                </SvgIcon>
              }
            >
              {intl.formatMessage({
                id: 'features.activities.header.refresh',
              })}
            </Button>
          </Tooltip>,
        ]
      : []);

  return (
    <Header
      scrollableDivRef={scrollableDivRef}
      title={title}
      description={description}
      breadcrumbs={
        <SettingsBreadcrumbs
          breadcrumbs={[
            {
              to: activitiesPath(),
              label: intl.formatMessage({
                id: 'containers.settings.activities.header.title',
              }),
            },
            {
              label: title,
            },
          ]}
        />
      }
      tabs={
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab
            value={activitiesExtensionsPath()}
            label={intl.formatMessage({
              id: 'features.activities.tabs.extensions',
            })}
            data-cy="activities-extensions-tab"
          />

          <Tab
            value={activitiesRulesPath()}
            label={intl.formatMessage({
              id: 'features.activities.tabs.rules',
            })}
            data-cy="activities-rules-tab"
          />
        </Tabs>
      }
      buttons={headerButtons}
    />
  );
};
